import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useQueryClient } from '@tanstack/react-query';
import { DatalistInput } from '../components/dataListInput';
import { useSettings } from '../../../context/SettingContext';
import type { LegalAdviceFormThreeData } from '../../../types/LegalAdvices/LegaladviceFormThree.type';
import { fetchLegalAdviceFormThreeById, saveLegalAdviceFormThreeDraft, submitLegalAdviceFormThreeForm } from '../../../services/LegalAdviceThreeService';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';

export const INITIAL_LEGAL_ADVICE_FORM_THREE_DATA: LegalAdviceFormThreeData = {
  id: '',
  documentNumber: '',
  status: 'DRAFT',
  rejectionReason: '',
  submitDate: '',
  attachments: [],

  // Legal Advice Form Three specific fields
  regNo: '',
  regSerialNumber: '',
  courtName: '',
  caseNumber: '',
  plaintiffName: '',
  defendantNameAndNrc: '',
  defendantFatherName: '',
  defendantAddress: '',
  policeStationAndFirstReportNo: '',
  briefFacts: '',
  chargeSectionAndDate: '',
  judgmentSentenceAndDate: '',
  appealedSectionAndDate: '',
  appellateDecisionAndDate: '',
  acquittalGrounds: '',
  judgmentCorrectness: '',
  revisionRequired: '',
  reasons: '',

  prosecutorSignatureImage: '',
  prosecutorName: '',
  prosecutorSubmissionDate: '',

  responsibleOfficerOpinion: '',
  officerSignatureImage: '',
  officerName: '',
  officerOffice: '',
  officerSubmissionDate: '',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<LegalAdviceFormThreeData>;
}

export function LegalFormThree({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<LegalAdviceFormThreeData>({
    ...INITIAL_LEGAL_ADVICE_FORM_THREE_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
   const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);
  const [activeSigningRole, setActiveSigningRole] = useState<'prosecutor' | 'USER' | null>(null);

  const { settings } = useSettings();
  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  // const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null

  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchLegalAdviceFormThreeById(id)
      .then((data: LegalAdviceFormThreeData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const isFormSigned = Boolean(
    formData.officerSignatureImage?.trim() && formData.prosecutorSignatureImage?.trim() && 
    (formData.prosecutorName?.trim() || formData.officerName?.trim())
  );

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['legalFormThree'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/legal-form-one');
    }
  };

  // Form Field Event Handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearDate = (name: keyof LegalAdviceFormThreeData) => {
      if (!isEditable) return;
      setFormData((prev) => ({ ...prev, [name]: '' }));
    };


  const renderDatePicker = (
    name: Extract<keyof LegalAdviceFormThreeData, string>
  ) => (
    <FormDatePicker
      name={name}
      value={(formData?.[name] as string) || ''}
      isEditable={isEditable}
      onChange={handleChange}
      onClear={handleClearDate}
    />
  );

  // const handleClearDate = (name: keyof LegalAdviceFormThreeData) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({ ...prev, [name]: '' }));
  // };

  const handleClearProsecutorSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, prosecutorSignatureImage: '' }));
  };

  const handleClearOfficerSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, officerSignatureImageSignatureImage: '' }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };


  const handleOpenSignatureModal = (role: 'prosecutor' | 'USER') => {
    if (!isEditable) return;
    setActiveSigningRole(role);
    setIsSigning(true);
  };


  const handleSaveSignature = () => {
  if (sigCanvasRef.current) {
    // Replace getTrimmedCanvas() with getCanvas()
    const canvas = sigCanvasRef.current.getCanvas();
    const dataUrl = canvas.toDataURL('image/png');
    
    if (activeSigningRole === 'prosecutor') {
      setFormData((prev) => ({ ...prev, prosecutorSignatureImage: dataUrl }));
    } else {
      setFormData((prev) => ({ ...prev, officerSignatureImage: dataUrl }));
    }
    
    setIsSigning(false);
    setActiveSigningRole(null);
  }
};

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<LegalAdviceFormThreeData> = { ...formData, status: 'DRAFT' };

      const response = await saveLegalAdviceFormThreeDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.prosecutorSubmissionDate?.trim() || !formData.officerSubmissionDate?.trim()) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    const nameOfOfficer = (formData.officerName || '').trim();
    if (!formData.officerSignatureImage?.trim() || !nameOfOfficer) {
      alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    const nameOfProsecutor = (formData.prosecutorName || '').trim();
    if (!formData.prosecutorSignatureImage?.trim() || !nameOfProsecutor) {
      alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      const payload: LegalAdviceFormThreeData = {
        ...formData,
        id: targetId || '',
        officerName: nameOfOfficer,
        prosecutorName: nameOfProsecutor,
        status: 'APPROVED',
      };

      const response = await saveLegalAdviceFormThreeDraft(targetId || null, payload);
      const newId = response?.id || targetId;

      setFormData((prev) => ({
        ...prev,
        id: newId,
        officerName: nameOfOfficer,
        prosecutorName: nameOfProsecutor,
        status: 'APPROVED',
      }));

      setIsApproved(true); // Locks form editing
      alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to approve and save:', err);
      alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (e: React.MouseEvent,
    targetLevels?: GeoLevel[] | null
  ) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.prosecutorSubmissionDate?.trim() || !formData.officerSubmissionDate?.trim()) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

     // Use passed levels if available; otherwise use multi-select state
    const levelsToSubmit =
      targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

    // Validate that at least one checkbox level is selected
    if (!levelsToSubmit || levelsToSubmit.length === 0) {
      alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      if (!targetId) {
        const draftResponse = await saveLegalAdviceFormThreeDraft(null, { ...formData, status: 'DRAFT' });
        targetId = draftResponse?.id;
        if (!targetId) {
          throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
        }

        setFormData((prev) => ({
          ...prev,
          ...draftResponse,
          id: targetId!,
        }));
      }

      const nameOfProsecutor = (formData.prosecutorName || '').trim();
      const nameOfOfficer = (formData.officerName || '').trim();
      const hasSigned = Boolean(formData.officerSignatureImage?.trim() && nameOfOfficer && formData.prosecutorSignatureImage?.trim() && nameOfProsecutor);
      // const nextStatus = hasSigned ? 'APPROVED' : 'SUBMITTED';
  
      if(!hasSigned){
        throw new Error('လက်မှတ်ထိုးပြီးမှသာ တင်ပြနိုင်ပါသည်');
      }
      const payload: LegalAdviceFormThreeData = {
        ...formData,
        id: targetId,
        officerName: nameOfOfficer,
        prosecutorName: nameOfProsecutor,
        status: 'SUBMITTED',
        submitDate: formData.officerSubmissionDate,
        targetGeoLevels: levelsToSubmit, // Array of selected levels
      };

      const response = await submitLegalAdviceFormThreeForm(targetId, payload);

      setFormData((prev) => ({
        ...prev,
        id: targetId,
        status: response?.status || 'SUBMITTED',
      }));

      setIsApproved(true); // Locks form editing after submission
      alert(
        response?.status ==='SUBMITTED'
          ?  'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)' : 'တင်ပြမှုမအောင်မြင်ပါ'
      );
      navigate('/legal-form-one');
    } catch (err: any) {
      console.error('Failed to submit form:', err);
      alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
  const status = formData.status || 'DRAFT';

  // Extract and format Geo Level text
  const rawGeo = formData.targetGeoLevels || formData.geoLevel;
  const formatGeo = (geo: any) => {
    if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
    if (Array.isArray(geo)) {
      return geo
        .map((g) => GEO_LEVEL_MAP[g] || g)
        .filter(Boolean)
        .join(' | ');
    }
    return '';
  };
  const geoText = formatGeo(rawGeo);

  const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
  > = {
    DRAFT: {
      label: 'မူကြမ်း',
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    APPROVED: {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-600',
    },
    SUBMITTED: {
      label: 'တင်ပြပြီး',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    UNDER_REVIEW: {
      label: 'ကြည့်ရှုထားသည်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
    ATTACHED: {
      label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
  };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

  return (
      <PageLayoutWrapper
        handleBackNavigation={handleBackNavigation}
        renderStageProgression={renderStageProgression}
        isEditable={isEditable}
        isPreview={isPreview}
        readOnlyMessage={
          formData.status === 'APPROVED'
            ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : formData.status === 'SUBMITTED'
              ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
              : undefined
        }
        setIsPreview={setIsPreview}
      >
        {/* A4 Sheet Container */}
        {/* A4 Sheet Container */}
      <div className="w-full max-w-[800px] mx-auto bg-white shadow-lg border border-gray-300 p-12 text-black leading-relaxed relative text-sm md:text-base">
        <div className="absolute top-4 right-4 text-xs font-bold">ဥပရအမှုလိုက်ပုံစံ (၃)</div>

        {/* Header Title Section */}
        <div className="text-center space-y-1 mt-6">
          <h1 className="text-base md:text-lg font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>
          <h2 className="text-sm md:text-base font-bold">ဥပဒေရေးရာဝန်ကြီးဌာန</h2>
          <div className="flex justify-center items-center gap-2">
                        <DatalistInput
                          name="officerName"
                          value={formData.officerName!}
                          onChange={handleChange}
                          placeholder=""
                          options={OFFICE_OPTIONS}
                        />
                        <span className="w-24 text-left">ရုံး</span>
                      </div>
          <div className="pt-4 flex flex-col items-center">
            
          <div className="border border-black px-6 py-3 inline-block bg-transparent space-y-2">
            <div className="flex items-center justify-center gap-1 font-bold flex-wrap">
              <span>ဥပရမှတ်ပုံတင် (</span>
                <input 
                  type="text" 
                  name="regNo"
                  value={formData.regNo || ''}
                  onChange={handleChange}
                  placeholder=""
                  className="w-16 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
                />
                <span>) အမှတ်စဉ်</span>
                <input 
                  type="text" 
                  name="regSerialNumber"
                  value={formData.regSerialNumber || ''}
                  onChange={handleChange}
                  placeholder=""
                  className="w-24 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
                />
              </div>
              <p className="font-bold text-base text-center">ပြစ်မှုဆိုင်ရာမူလမှု ပြီးပြတ်ချက်ပုံစံ</p>
            </div>
          </div>
        </div>

        {/* Document Form Fields */}
        <div className="mt-8 space-y-5 text-justify">
          
          {/* ၁။ တရားရုံး (Using Datalist Dropdown like Form 1) */}
          <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
            <span className="font-bold whitespace-nowrap">၁။ တရားရုံးအမည်</span>
            <DatalistInput
              name="courtName"
              value={formData.courtName!}
              onChange={handleChange}
              placeholder="တရားရုံး ရွေးချယ်ရန်"
              options={OFFICE_OPTIONS}
              className="flex-1 bg-transparent border-b border-black focus:outline-none py-0.5 text-center min-w-[200px]"
            />
          </div>

          {/* ၂။ အမှုအမှတ် */}
          <div className="flex items-center gap-2">
            <span className="font-bold whitespace-nowrap">၂။ အမှုအမှတ် -</span>
            <input 
              type="text" 
              name="caseNumber"
              value={formData.caseNumber}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2"
            />
          </div>

          {/* ၃။ တရားလိုအမည် */}
          <div className="flex items-center gap-2">
            <span className="font-bold whitespace-nowrap">၃။ တရားလိုအမည် -</span>
            <input 
              type="text" 
              name="plaintiffName"
              value={formData.plaintiffName}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2"
            />
          </div>

          {/* ၄။ တရားခံအမည်နှင့် နိုင်ငံသားစိစစ်ရေးကတ်ပြားအမှတ် */}
          <div className="flex items-start gap-2">
            <span className="font-bold whitespace-nowrap">၄။ တရားခံအမည်နှင့် နိုင်ငံသားစိစစ်ရေးကတ်ပြားအမှတ်/နိုင်ငံခြားသားမှတ်ပုံတင်အမှတ် -</span>
            <input 
              type="text" 
              name="defendantNameAndNrc"
              value={formData.defendantNameAndNrc}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2"
            />
          </div>

          {/* ၅။ တရားခံ၏ အဘအမည် */}
          <div className="flex items-center gap-2">
            <span className="font-bold whitespace-nowrap">၅။ တရားခံ၏ အဘအမည် -</span>
            <input 
              type="text" 
              name="defendantFatherName"
              value={formData.defendantFatherName}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2"
            />
          </div>

          {/* ၆။ တရားခံ၏နေရပ်လိပ်စာ */}
          <div className="flex items-center gap-2">
            <span className="font-bold whitespace-nowrap">၆။ တရားခံ၏နေရပ်လိပ်စာ -</span>
            <input 
              type="text" 
              name="defendantAddress"
              value={formData.defendantAddress}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2"
            />
          </div>

          {/* ၇။ ရဲစခန်းအမည် ပထမသတင်းပေးချက်အမှတ်နှင့်ရက်စွဲ */}
          <div className="flex items-start gap-2">
            <span className="font-bold whitespace-nowrap">၇။ ရဲစခန်း အမည် ပထမ သတင်းပေးချက်အမှတ်နှင့် ရက်စွဲ -</span>
            <input 
              type="text" 
              name="policeStationAndFirstReportNo"
              value={formData.policeStationAndFirstReportNo}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2"
            />
          </div>

          {/* ၈။ အမှုဖြစ်စဉ်အကျဉ်း */}
          <div className="space-y-1">
            <span className="font-bold block">၈။ အမှုဖြစ်စဉ်အကျဉ်း -</span>
            <textarea 
              name="briefFacts"
              value={formData.briefFacts}
              onChange={handleChange}
              rows={4}
              className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm"
            />
          </div>

          {/* ၉။ တရားစွဲဆိုတင်ပို့သည့် ဥပဒေပုဒ်မနှင့် ရက်စွဲ */}
          <div className="flex items-center gap-2">
            <span className="font-bold whitespace-nowrap">၉။ တရားစွဲဆိုတင်ပို့သည့် ဥပဒေပုဒ်မနှင့် ရက်စွဲ -</span>
            <input 
              type="text" 
              name="chargeSectionAndDate"
              value={formData.chargeSectionAndDate}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2"
            />
          </div>

          {/* ၁၀။ စွဲချက်တင်မိန့်လွှတ်သည့် စီရင်ချက် */}
          <div className="space-y-1">
            <span className="font-bold block">၁၀။ စွဲချက်မတင်မီလွှတ်သည့် စီရင်ချက်၊ အမိန့်အကျဉ်းနှင့်ရက်စွဲ (တရားရုံးကလွှတ်သည့် အဓိက အကြောင်းပြချက် ဖော်ပြရန်)</span>
            <textarea 
              name="judgmentSentenceAndDate"
              value={formData.judgmentSentenceAndDate}
              onChange={handleChange}
              rows={2}
              className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm"
            />
          </div>

          {/* ၁၁။ တရားရုံးက စွဲချက်တင်သည့် ဥပဒေ ပုဒ်မနှင့် ရက်စွဲ */}
          <div className="flex items-center gap-2">
            <span className="font-bold whitespace-nowrap">၁၁။ တရားရုံးက စွဲချက်တင်သည့် ဥပဒေ ပုဒ်မနှင့် ရက်စွဲ -</span>
            <input 
              type="text" 
              name="appealedSectionAndDate"
              value={formData.appealedSectionAndDate}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2"
            />
          </div>

          {/* ၁၂။ တရားရုံး၏ အပြီးသတ်စီရင်ချက် */}
          <div className="flex items-center gap-2">
            <span className="font-bold whitespace-nowrap">၁၂။ တရားရုံး၏ အပြီးသတ်စီရင်ချက်/အမိန့်အကျဉ်းနှင့် ရက်စွဲ -</span>
            <input 
              type="text" 
              name="appellateDecisionAndDate"
              value={formData.appellateDecisionAndDate}
              onChange={handleChange}
              className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2"
            />
          </div>

          {/* ၁၃။ ပြစ်ဒဏ်ပေးသည့်ဥပဒေ ပုဒ်မ */}
          <div className="space-y-1">
            <span className="font-bold block">၁၃။ ပြစ်ဒဏ်ပေးသည့်ဥပဒေ ပုဒ်မ (အပြီးအပြတ်လွတ်ပါကလွတ်သည့် အဓိက အကြောင်းပြချက် ဖော်ပြရန်) -</span>
            <textarea 
              name="acquittalGrounds"
              value={formData.acquittalGrounds}
              onChange={handleChange}
              rows={2}
              className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm"
            />
          </div>

          {/* ၁၄။ အမှုလိုက် ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် အကျဉ်းချုပ် */}
          <div className="space-y-3 pt-2">
            <span className="font-bold block">၁၄။ အမှုလိုက် ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် အကျဉ်း --</span>
            
            <div className="pl-6 space-y-3">
              <div className="flex items-center gap-2">
                <span>(က) စီရင်ချက်/အမိန့်မှန်ကန်မှု</span>
                <input 
                  type="text" 
                  name="judgmentCorrectness"
                  placeholder="ရှိ / မရှိ"
                  value={formData.judgmentCorrectness}
                  onChange={handleChange}
                  className="w-32 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
                />
              </div>

              <div className="flex items-center gap-2">
                <span>(ခ) ပြင်ဆင်မှု သို့မဟုတ် အယူခံမှုတက်ရောက်ရန်လိုအပ်ခြင်း</span>
                <input 
                  type="text" 
                  name="revisionRequired"
                  placeholder="ရှိ / မရှိ"
                  value={formData.revisionRequired}
                  onChange={handleChange}
                  className="w-32 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
                />
              </div>

              <div className="space-y-1">
                <span>(ဂ) အကျိုးအကြောင်း ဖော်ပြချက် -</span>
                <textarea 
                  name="reasons"
                  value={formData.reasons}
                  onChange={handleChange}
                  rows={2}
                  className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm"
                />
              </div>
            </div>

            {/* Section 14 Signature Box */}
            <div className="mt-6 flex flex-col items-end pr-6">
              <div className="w-80 flex flex-col space-y-2">
                <div className="flex items-center">
                  <span className="w-20 text-right text-sm mr-2">လက်မှတ်</span>
                  {/* Prosecutor Signature Click Area */}
                  <div 
                    onClick={() => handleOpenSignatureModal('prosecutor')}
                    className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 transition relative group"
                  >
                    {formData.prosecutorSignatureImage ? (
                      <img src={formData.prosecutorSignatureImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" />
                    ) : (
                      <span className="text-gray-400 text-xs">✍️ နှိပ်၍လက်မှတ်ထိုးပါ</span>
                    )}
                  </div>
                  {formData.prosecutorSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearProsecutorSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="flex items-center">
                  <span className="w-20 text-right text-sm mr-2">အမည်</span>
                  <input
                    type="text"
                    name="prosecutorName"
                    value={formData.prosecutorName}
                    onChange={handleChange}
                    className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
                  />
                </div>
                <p className="text-sm font-bold text-center pt-1">အမှုလိုက်ဥပဒေအရာရှိ</p>
                
                <div className="flex items-center">
                  <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">ရက်စွဲ</span>
                  <div className="flex-grow">{renderDatePicker('prosecutorSubmissionDate')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ၁၅။ တာဝန်ခံဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် */}
          <div className="space-y-3 pt-4 border-t border-gray-200">
            <span className="font-bold block">၁၅။ တာဝန်ခံဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် -</span>
            <textarea 
              name="responsibleOfficerOpinion"
              value={formData.responsibleOfficerOpinion}
              onChange={handleChange}
              rows={3}
              className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm"
            />

            {/* Section 15 Signature Box */}
            <div className="mt-6 flex flex-col items-end pr-6">
              <div className="w-80 flex flex-col space-y-2">
                <div className="flex items-center">
                  <span className="w-20 text-right text-sm mr-2">လက်မှတ်</span>
                  {/* Officer / User Signature Click Area */}
                  <div 
                    onClick={() => handleOpenSignatureModal('USER')}
                    className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 transition relative group"
                  >
                    {formData.officerSignatureImage ? (
                      <img src={formData.officerSignatureImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" />
                    ) : (
                      <span className="text-gray-400 text-xs">✍️ နှိပ်၍လက်မှတ်ထိုးပါ</span>
                    )}
                  </div>
                  {formData.prosecutorSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearOfficerSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="flex items-center">
                  <span className="w-20 text-right text-sm mr-2">အမည်</span>
                  <input
                    type="text"
                    name="officerName"
                    value={formData.officerName}
                    onChange={handleChange}
                    className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
                  />
                </div>
                <p className="text-sm font-bold text-center pt-1">တာဝန်ခံဥပဒေအရာရှိ</p>
                
                <div className="flex items-center">
                  <input
                    type="text"
                    name="officerOffice"
                    placeholder="ဥပဒေရုံးအမည်"
                    value={formData.officerOffice}
                    onChange={handleChange}
                    className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center text-sm"
                  />
                  <span className="text-sm ml-1">ဥပဒေရုံး</span>
                </div>

                <div className="flex items-center">
                  <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                  <div className="flex-grow">{renderDatePicker('officerSubmissionDate')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
                  {/* Form Action Buttons Component Integration */}
                    <FormActionButtons
                      isPreview={isPreview}
                      isEditable={isEditable}
                      isSubmitting={isSubmitting}
                      isFormSigned={isFormSigned}
                      isApproved={isApproved}
                      targetGeoLevels={formData.targetGeoLevels}
                      currentGeoLevel={currentUser?.scopeLevel}
                      selectedGeoLevels={selectedGeoLevels}
                      onGeoLevelChange={setSelectedGeoLevels}
                      onCancel={handleBackNavigation}
                      onSaveDraft={handleSaveDraft}
                      onApproveAndSave={handleApproveAndSave}
                      onSubmitToSuperior={handleSubmitToSuperior}
                    />
          
                  {/* Attachment Section */}
                  {/* <AttachmentSection
                    attachments={formData.attachments}
                    isEditable={isEditable}
                    isPreview={isPreview}
                    onFileChange={handleFileChange}
                    onRemoveFile={handleRemoveFile}
                  /> */}
          
                  {/* Signature Modal */}
                  <SignatureModal
                    isOpen={isSigning}
                    sigCanvasRef={sigCanvasRef}
                    onClose={() => {
                      setIsSigning(false);
                      setActiveSigningRole(null);
                    }}
                    onClear={() => sigCanvasRef.current?.clear()}
                    onSave={handleSaveSignature}
                  />

        </div>
      </div>
      </PageLayoutWrapper>
    );
  }